/**
 * Shipping Estimator
 * Handles postcode-based shipping estimates with stock-aware delivery dates
 * Fetches live shipping rates from Shippit via Cloudflare Worker proxy
 * @version 2026-03-03-a - Added stock override tag support for supplier delay overrides
 */

(function() {
  'use strict';

  // Cloudflare Worker URL for Shippit quotes and GWA API proxy
  const SHIPPIT_PROXY_URL = 'https://silent-poetry-311d.cassadmin.workers.dev/';

  // GWA (Caroma) API integration - vendors whose stock is checked via GWA API
  const GWA_VENDORS = ['caroma'];
  const gwaCache = new Map();

  // Phoenix supplier integration - vendors whose stock is checked via static JSON
  const PHOENIX_VENDORS = ['phoenix', 'phoenix tapware'];
  let supplierStockData = null;
  let supplierStockLoading = null;

  // Product-level attribute cache — survives morph so vendor/tags can be restored if morph empties them
  var productAttributeCache = {};
  // Timestamp of last event-driven update — prevents URL polling from double-firing
  var lastEventDrivenUpdate = 0;

  /**
   * Vendor Lead Times (in business days)
   * These are supplier lead times + picking (1-2 days) + shipping to customer (2-3 days)
   * Formula: Supplier Lead Time + 3-5 days (picking + shipping)
   *
   * Categories:
   * - fast: 8-12 business days (5-7 supplier + 3-5 processing)
   * - medium: 10-15 business days (7-10 supplier + 3-5 processing)
   * - standard: 15-20 business days (1-2 weeks supplier + 3-5 processing)
   * - extended: 22-30 business days (3-4 weeks supplier + 3-5 processing)
   * - long: 35-50 business days (4-6 weeks supplier + 3-5 processing)
   * - veryLong: 55-75 business days (1-12 weeks supplier + processing)
   * - custom: 65-160 business days (12-30 weeks supplier + processing)
   */
  const VENDOR_LEAD_TIMES = {
    // 5-7 Business Days suppliers (8-12 days to customer)
    'parisi': { min: 8, max: 12, label: '8-12 business days' },
    'argent': { min: 8, max: 12, label: '8-12 business days' },
    'bidet shop': { min: 8, max: 12, label: '8-12 business days' },
    'nero': { min: 8, max: 12, label: '8-12 business days' },
    'fienza': { min: 8, max: 12, label: '8-12 business days' },
    'phoenix': { min: 8, max: 12, label: '8-12 business days' },
    'phoenix tapware': { min: 8, max: 12, label: '8-12 business days' },
    'turner hastings': { min: 8, max: 12, label: '8-12 business days' },
    'abey': { min: 8, max: 12, label: '8-12 business days' },
    'thermogroup': { min: 8, max: 12, label: '8-12 business days' },
    'blanco': { min: 8, max: 12, label: '8-12 business days' },
    'caroma': { min: 8, max: 12, label: '8-12 business days' },
    'expella': { min: 8, max: 12, label: '8-12 business days' },
    'geberit': { min: 8, max: 12, label: '8-12 business days' },
    'greens': { min: 8, max: 12, label: '8-12 business days' },
    'hotwire': { min: 8, max: 12, label: '8-12 business days' },
    'insinkerator': { min: 8, max: 12, label: '8-12 business days' },
    'ixl': { min: 8, max: 12, label: '8-12 business days' },
    'kwc franke': { min: 8, max: 12, label: '8-12 business days' },
    'mixx tapware': { min: 8, max: 12, label: '8-12 business days' },
    'puretec': { min: 8, max: 12, label: '8-12 business days' },
    'rinnai': { min: 8, max: 12, label: '8-12 business days' },
    'rheem': { min: 8, max: 12, label: '8-12 business days' },
    'shaws': { min: 8, max: 12, label: '8-12 business days' },
    'luxe by design': { min: 8, max: 12, label: '8-12 business days' },
    'studio bagno': { min: 8, max: 12, label: '8-12 business days' },
    'velux': { min: 8, max: 12, label: '8-12 business days' },
    'villeroy & boch': { min: 8, max: 12, label: '8-12 business days' },
    'villeroy and boch': { min: 8, max: 12, label: '8-12 business days' },

    // 7-10 Business Days suppliers (10-15 days to customer)
    'avenir': { min: 10, max: 15, label: '10-15 business days' },
    'hansa': { min: 10, max: 15, label: '10-15 business days' },
    'hansgrohe': { min: 10, max: 15, label: '10-15 business days' },
    'oliveri': { min: 10, max: 15, label: '10-15 business days' },

    // 1-2 Weeks suppliers (10-17 days to customer)
    'ledin': { min: 10, max: 17, label: '2-2.5 weeks' },

    // 2-3 Weeks suppliers (18-25 days to customer)
    'euro': { min: 18, max: 25, label: '3-4 weeks' },
    'rifco': { min: 18, max: 25, label: '3-4 weeks' },
    'sussex': { min: 18, max: 25, label: '3-4 weeks' },

    // Zip/Billi - varies by finish (Chrome vs coloured finishes)
    // Chrome: 1 week supplier = 2-2.5 weeks to customer
    // Matte Black: 3 weeks supplier = 3.5-4 weeks to customer
    // Other finishes: 4-6 weeks supplier = 5-7 weeks to customer
    // Default to Matte Black timing as it's common
    'zip': { min: 22, max: 30, label: '3.5-4 weeks', finishBased: true },
    'billi': { min: 22, max: 30, label: '3.5-4 weeks', finishBased: true },

    // 4-6 Weeks suppliers (5-7 weeks to customer)
    'vanities': { min: 28, max: 38, label: '5-7 weeks' },
    'marquis': { min: 28, max: 38, label: '5-7 weeks' },

    // 1-12 Weeks suppliers (2-13 weeks to customer)
    'rainware': { min: 13, max: 68, label: '2-13 weeks' },

    // Kaldewei - 14-16 weeks (made to order baths)
    'kaldewei': { min: 73, max: 83, label: '14-16 weeks' },

    // Up to 30 Weeks suppliers (up to 31 weeks to customer)
    'victoria + albert': { min: 65, max: 160, label: 'Up to 31 weeks' },
    'victoria and albert': { min: 65, max: 160, label: 'Up to 31 weeks' },
    'victoria+albert': { min: 65, max: 160, label: 'Up to 31 weeks' }
  };

  // Default lead time for vendors not in the list (fallback to generic 10-15 days)
  const DEFAULT_VENDOR_LEAD_TIME = { min: 10, max: 15, label: '10-15 business days' };

  /**
   * Get vendor-specific lead time
   * @param {string} vendor - The product vendor name
   * @returns {object} Lead time object with min, max, and label
   */
  function getVendorLeadTime(vendor) {
    if (!vendor) return DEFAULT_VENDOR_LEAD_TIME;
    const vendorLower = vendor.toLowerCase().trim();
    return VENDOR_LEAD_TIMES[vendorLower] || DEFAULT_VENDOR_LEAD_TIME;
  }

  /**
   * Check if a vendor uses GWA API for stock checks
   * @param {string} vendor - The product vendor name
   * @returns {boolean}
   */
  function isGwaVendor(vendor) {
    if (!vendor) return false;
    return GWA_VENDORS.includes(vendor.toLowerCase().trim());
  }

  /**
   * Check if a vendor uses Phoenix supplier stock data
   * @param {string} vendor - The product vendor name
   * @returns {boolean}
   */
  function isPhoenixVendor(vendor) {
    if (!vendor) return false;
    return PHOENIX_VENDORS.includes(vendor.toLowerCase().trim());
  }

  /**
   * Parse a stock override tag value like "4-6 weeks" or "2 weeks"
   * @param {string} value - The stock override string (e.g. "4-6 weeks", "2 weeks", "1-3 weeks")
   * @returns {object|null} { minWeeks, maxWeeks, minDays, maxDays, isBackorder } or null if unparseable
   */
  function parseStockOverride(value) {
    if (!value) return null;
    var cleaned = value.toLowerCase().replace('weeks', '').replace('week', '').trim();
    var parts = cleaned.split('-').map(function(p) { return parseInt(p.trim(), 10); });
    var minWeeks, maxWeeks;

    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      minWeeks = parts[0];
      maxWeeks = parts[1];
    } else if (parts.length === 1 && !isNaN(parts[0])) {
      minWeeks = parts[0];
      maxWeeks = parts[0];
    } else {
      return null;
    }

    // Convert weeks to business days + 3-5 days processing/shipping
    var minDays = (minWeeks * 5) + 3;
    var maxDays = (maxWeeks * 5) + 5;

    return {
      minWeeks: minWeeks,
      maxWeeks: maxWeeks,
      minDays: minDays,
      maxDays: maxDays,
      isBackorder: maxWeeks > 3
    };
  }

  /**
   * Fetch supplier stock JSON (loaded once per page, cached in memory)
   * @param {string} url - The CDN URL for supplier-stock.json (from data attribute)
   * @returns {object|null} The full stock status map, or null on failure
   */
  async function fetchSupplierStock(url) {
    if (!url) return null;

    // Return cached data if already loaded
    if (supplierStockData) return supplierStockData;

    // If already loading, wait for that request to complete
    if (supplierStockLoading) return supplierStockLoading;

    // console.log('[ShippingEstimator] Fetching supplier stock JSON...');
    supplierStockLoading = (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          console.error('[ShippingEstimator] Supplier stock fetch error:', response.status);
          return null;
        }
        const data = await response.json();
        supplierStockData = data;
        // console.log('[ShippingEstimator] Supplier stock loaded. Updated:', data._updated, 'Source:', data._source);
        return data;
      } catch (error) {
        console.error('[ShippingEstimator] Supplier stock fetch error:', error);
        return null;
      } finally {
        supplierStockLoading = null;
      }
    })();

    return supplierStockLoading;
  }

  /**
   * Look up a SKU in the supplier stock data
   * @param {object} stockData - The supplier stock JSON object
   * @param {string} sku - The product SKU to look up
   * @returns {string|null} Stock status ("In Stock", "2-4 Weeks", "Enquiry") or null
   */
  function getSupplierStockStatus(stockData, sku) {
    if (!stockData || !sku) return null;
    return stockData[sku] || null;
  }

  /**
   * Fetch GWA Available Quantity for a SKU
   * @param {string} sku - The product SKU/item code
   * @returns {object|null} Stock data or null on failure
   */
  async function fetchGwaStock(sku) {
    if (!sku) return null;

    const cacheKey = `gwa_stock_${sku}`;
    if (gwaCache.has(cacheKey)) {
      // console.log('[ShippingEstimator] Using cached GWA stock for', sku);
      return gwaCache.get(cacheKey);
    }

    // console.log('[ShippingEstimator] Fetching GWA stock for SKU:', sku);
    try {
      const response = await fetch(SHIPPIT_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'gwa_stock', itemCode: sku })
      });

      if (!response.ok) {
        console.error('[ShippingEstimator] GWA stock API error:', response.status);
        return null;
      }

      const data = await response.json();
      if (data.success) {
        // console.log('[ShippingEstimator] GWA stock result:', data.itemCode, 'Sydney qty:', data.quantity, 'Total qty:', data.totalQuantity || 'N/A');
        gwaCache.set(cacheKey, data);
        return data;
      }

      console.warn('[ShippingEstimator] GWA stock API returned failure:', data.error);
      return null;
    } catch (error) {
      console.error('[ShippingEstimator] GWA stock fetch error:', error);
      return null;
    }
  }

  /**
   * Fetch GWA Delivery Estimate for a SKU
   * @param {string} sku - The product SKU/item code
   * @param {number} qty - Requested quantity
   * @returns {object|null} Delivery estimate or null on failure
   */
  async function fetchGwaDeliveryEstimate(sku, qty = 1) {
    if (!sku) return null;

    const cacheKey = `gwa_delivery_${sku}_${qty}`;
    if (gwaCache.has(cacheKey)) {
      // console.log('[ShippingEstimator] Using cached GWA delivery for', sku);
      return gwaCache.get(cacheKey);
    }

    // console.log('[ShippingEstimator] Fetching GWA delivery estimate for SKU:', sku);
    try {
      const response = await fetch(SHIPPIT_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'gwa_delivery', itemCode: sku, qty: qty })
      });

      if (!response.ok) {
        console.error('[ShippingEstimator] GWA delivery API error:', response.status);
        return null;
      }

      const data = await response.json();
      if (data.success) {
        // console.log('[ShippingEstimator] GWA delivery estimate:', data.estimatedShipDate);
        gwaCache.set(cacheKey, data);
        return data;
      }

      console.warn('[ShippingEstimator] GWA delivery API returned failure:', data.error);
      return null;
    } catch (error) {
      console.error('[ShippingEstimator] GWA delivery fetch error:', error);
      return null;
    }
  }

  // Supported postcode zones
  const SHIPPING_ZONES = {
    sydney: {
      name: 'Sydney Metro',
      postcodes: [
        [2000, 2234],
        [2555, 2574],
        [2745, 2770],
      ],
      state: 'NSW',
      city: 'Sydney',
      inStockDays: { min: 3, max: 5 },
      supplierDays: { min: 5, max: 10 }
    },
    melbourne: {
      name: 'Melbourne Metro',
      postcodes: [
        [3000, 3211],
        [3335, 3341],
        [3427, 3442],
        [3750, 3810],
      ],
      state: 'VIC',
      city: 'Melbourne',
      inStockDays: { min: 4, max: 6 },
      supplierDays: { min: 6, max: 12 }
    },
    brisbane: {
      name: 'Brisbane Metro',
      postcodes: [
        [4000, 4209],
        [4300, 4347],
        [4500, 4575],
      ],
      state: 'QLD',
      city: 'Brisbane',
      inStockDays: { min: 4, max: 6 },
      supplierDays: { min: 6, max: 12 }
    },
    newcastle: {
      name: 'Newcastle',
      postcodes: [
        [2259, 2264],
        [2280, 2330],
      ],
      state: 'NSW',
      city: 'Newcastle',
      inStockDays: { min: 3, max: 5 },
      supplierDays: { min: 5, max: 10 }
    },
    wollongong: {
      name: 'Wollongong',
      postcodes: [
        [2500, 2535],
      ],
      state: 'NSW',
      city: 'Wollongong',
      inStockDays: { min: 3, max: 5 },
      supplierDays: { min: 5, max: 10 }
    },
    canberra: {
      name: 'Canberra',
      postcodes: [
        [2600, 2620],
        [2900, 2920],
      ],
      state: 'ACT',
      city: 'Canberra',
      inStockDays: { min: 4, max: 6 },
      supplierDays: { min: 6, max: 12 }
    },
    perth: {
      name: 'Perth Metro',
      postcodes: [
        [6000, 6214],
      ],
      state: 'WA',
      city: 'Perth',
      inStockDays: { min: 7, max: 10 },
      supplierDays: { min: 10, max: 15 }
    }
  };

  const STORAGE_KEY = 'cb_shipping_postcode';
  const ratesCache = new Map();

  // Global flag to prevent recursive postcodeUpdated event loops across all estimator instances
  let globalUpdatingPostcode = false;
  // Timestamp of last updateDisplay call to prevent rapid successive calls
  let lastUpdateTimestamp = 0;
  // Flag to track if document-level postcodeUpdated listener has been registered
  let postcodeListenerRegistered = false;
  // Global flag to prevent multiple estimators from all loading the saved postcode simultaneously
  let globalPostcodeLoadStarted = false;

  /**
   * Ensure body attributes are set from localStorage
   * This is critical for CSS fallback to work after DOM morphs
   * Always sets the attributes (not conditional) to ensure they persist
   */
  function ensureBodyAttributes() {
    try {
      const postcode = localStorage.getItem(STORAGE_KEY);
      const suburb = localStorage.getItem('cb_shipping_suburb');
      if (postcode) {
        document.body.setAttribute('data-has-postcode', postcode);
      }
      if (suburb) {
        document.body.setAttribute('data-has-suburb', suburb);
      }
    } catch (e) {
      // localStorage might not be available
    }
  }

  // Ensure body attributes on script load
  if (document.body) {
    ensureBodyAttributes();
  } else {
    document.addEventListener('DOMContentLoaded', ensureBodyAttributes);
  }

  // Track initialized containers using WeakSet (survives DOM morph)
  const initializedContainers = new WeakSet();

  // Allied Express free shipping postcodes (Sydney Metro)
  const ALLIED_EXPRESS_FREE_POSTCODES = new Set([
    '2000', '2006', '2007', '2008', '2009', '2010', '2011', '2015', '2016', '2017', '2018', '2019',
    '2020', '2021', '2022', '2023', '2024', '2025', '2026', '2027', '2028', '2029', '2030', '2031',
    '2032', '2033', '2034', '2035', '2036', '2037', '2038', '2039', '2040', '2041', '2042', '2043',
    '2044', '2045', '2046', '2047', '2048', '2049', '2050', '2060', '2061', '2062', '2063', '2064',
    '2065', '2066', '2067', '2068', '2069', '2070', '2071', '2072', '2073', '2074', '2075', '2076',
    '2077', '2079', '2080', '2081', '2082', '2083', '2084', '2085', '2086', '2087', '2088', '2089',
    '2090', '2092', '2093', '2094', '2095', '2096', '2097', '2099', '2100', '2101', '2102', '2103',
    '2104', '2106', '2107', '2110', '2111', '2112', '2113', '2114', '2115', '2116', '2117', '2118',
    '2119', '2120', '2121', '2122', '2125', '2126', '2127', '2128', '2129', '2130', '2131', '2132',
    '2133', '2134', '2135', '2136', '2137', '2138', '2140', '2141', '2142', '2143', '2144', '2145',
    '2146', '2147', '2148', '2150', '2151', '2152', '2153', '2154', '2155', '2156', '2157', '2158',
    '2159', '2160', '2161', '2162', '2163', '2164', '2165', '2166', '2167', '2170', '2171', '2172',
    '2173', '2175', '2176', '2177', '2178', '2179', '2190', '2191', '2192', '2193', '2194', '2195',
    '2196', '2197', '2198', '2199', '2200', '2203', '2204', '2205', '2206', '2207', '2208', '2209',
    '2210', '2211', '2212', '2213', '2214', '2216', '2217', '2218', '2219', '2220', '2221', '2222',
    '2223', '2224', '2225', '2226', '2227', '2228', '2229', '2230', '2231', '2232', '2233', '2234',
    '2555', '2556', '2558', '2559', '2560', '2563', '2564', '2565', '2566', '2567', '2570',
    '2745', '2747', '2749', '2750', '2752', '2753', '2754', '2756', '2758', '2759', '2760', '2761',
    '2762', '2763', '2765', '2766', '2767', '2768', '2770'
  ]);

  /**
   * Check if postcode qualifies for Allied Express free shipping
   */
  function isAlliedExpressFreePostcode(postcode) {
    return ALLIED_EXPRESS_FREE_POSTCODES.has(String(postcode));
  }

  /**
   * Find shipping zone for a postcode
   */
  function getZoneForPostcode(postcode) {
    const pc = parseInt(postcode, 10);
    if (isNaN(pc)) return null;

    for (const [zoneKey, zone] of Object.entries(SHIPPING_ZONES)) {
      for (const range of zone.postcodes) {
        if (pc >= range[0] && pc <= range[1]) {
          return { key: zoneKey, ...zone };
        }
      }
    }
    return null;
  }

  /**
   * Get state from any Australian postcode
   */
  function getStateFromPostcode(postcode) {
    const pc = parseInt(postcode, 10);
    if (pc >= 2000 && pc <= 2599) return 'NSW';
    if (pc >= 2619 && pc <= 2899) return 'NSW';
    if (pc >= 2921 && pc <= 2999) return 'NSW';
    if (pc >= 2600 && pc <= 2618) return 'ACT';
    if (pc >= 2900 && pc <= 2920) return 'ACT';
    if (pc >= 3000 && pc <= 3999) return 'VIC';
    if (pc >= 4000 && pc <= 4999) return 'QLD';
    if (pc >= 5000 && pc <= 5999) return 'SA';
    if (pc >= 6000 && pc <= 6999) return 'WA';
    if (pc >= 7000 && pc <= 7999) return 'TAS';
    if (pc >= 800 && pc <= 999) return 'NT';
    return 'NSW';
  }

  /**
   * Calculate business days from now
   */
  function addBusinessDays(startDate, days) {
    const date = new Date(startDate);
    let added = 0;

    while (added < days) {
      date.setDate(date.getDate() + 1);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        added++;
      }
    }

    return date;
  }

  /**
   * Format date as "Mon 20th Jan"
   */
  function formatDate(date) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = days[date.getDay()];
    const dayNum = date.getDate();
    const month = months[date.getMonth()];

    let suffix = 'th';
    if (dayNum === 1 || dayNum === 21 || dayNum === 31) suffix = 'st';
    else if (dayNum === 2 || dayNum === 22) suffix = 'nd';
    else if (dayNum === 3 || dayNum === 23) suffix = 'rd';

    return `${dayName} ${dayNum}${suffix} ${month}`;
  }

  /**
   * Get delivery date range string
   */
  function getDeliveryDateRange(isInStock, zone) {
    const today = new Date();
    const days = isInStock ? zone.inStockDays : zone.supplierDays;

    const startDate = addBusinessDays(today, days.min);
    const endDate = addBusinessDays(today, days.max);

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }

  /**
   * Format price in dollars
   */
  function formatPrice(cents) {
    const dollars = cents / 100;
    return dollars === 0 ? 'FREE' : `$${dollars.toFixed(2)}`;
  }

  /**
   * Fetch shipping rates from Shippit via Cloudflare Worker proxy
   * @param {string} postcode - Destination postcode
   * @param {string} state - Destination state
   * @param {string} suburb - Destination suburb name (required by Shippit)
   * @param {number} weight - Package weight in kg
   * @param {string} preferredCarrier - Optional: 'eparcel' for Australia Post items
   */
  /**
   * Fetch Sherpa same-day/next-business-day delivery quote
   * Only called for in-stock products with qty >= 3
   */
  async function fetchSherpaQuote(suburb, state, postcode) {
    try {
      const deliveryAddress = `${suburb}, ${state} ${postcode}, Australia`;
      const response = await fetch(SHIPPIT_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'sherpa_quote',
          delivery_address: deliveryAddress
        })
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[ShippingEstimator] Sherpa quote error:', error);
      return null;
    }
  }

  async function fetchShippingRates(postcode, state, suburb, weight, preferredCarrier) {
    const cacheKey = `${postcode}-${weight}-${preferredCarrier || 'any'}`;

    if (ratesCache.has(cacheKey)) {
      // console.log('[ShippingEstimator] Using cached rate for', cacheKey);
      return ratesCache.get(cacheKey);
    }

    // console.log('[ShippingEstimator] Fetching Shippit rates for postcode:', postcode, 'suburb:', suburb, 'state:', state, 'carrier:', preferredCarrier || 'any');

    try {
      const response = await fetch(SHIPPIT_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          postcode: postcode,
          state: state,
          suburb: suburb,
          weight: weight || 1
        })
      });

      // console.log('[ShippingEstimator] Shippit response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ShippingEstimator] Shippit error:', errorText);
        return null;
      }

      const data = await response.json();
      // console.log('[ShippingEstimator] Shippit data:', data);

      // Shippit returns { response: [...quotes], count: N }
      if (data.response && Array.isArray(data.response) && data.response.length > 0) {
        // Debug: log all quotes
        // console.log('[ShippingEstimator] All quotes:', data.response);

        // Debug: log each quote's structure to diagnose filtering issues
        data.response.forEach((q, i) => {
          // console.log(`[ShippingEstimator] Quote ${i}: courier=${q.courier_type}, success=${q.success}, quotes=${q.quotes ? q.quotes.length : 'undefined'}, price=${q.price}`);
        });

        // Filter for successful quotes only, excluding ClickAndCollect (not a delivery method)
        // Some Shippit responses have price directly on the quote object, not in a nested quotes array
        let successfulQuotes = data.response.filter(q =>
          q.success === true &&
          q.courier_type !== 'ClickAndCollect' &&
          ((q.quotes && q.quotes.length > 0) || q.price !== undefined)
        );

        // If preferred carrier is 'eparcel', filter for Eparcel/EparcelExpress only (Australia Post)
        if (preferredCarrier === 'eparcel') {
          const eparcelQuotes = successfulQuotes.filter(q =>
            q.courier_type === 'Eparcel' || q.courier_type === 'EparcelExpress'
          );
          if (eparcelQuotes.length > 0) {
            successfulQuotes = eparcelQuotes;
            // console.log('[ShippingEstimator] Filtered for Eparcel:', successfulQuotes);
          }
        }

        // console.log('[ShippingEstimator] Successful quotes:', successfulQuotes);

        if (successfulQuotes.length > 0) {
          // Flatten all quotes from successful courier types
          // Handle both nested quotes array and direct price on quote object
          const allQuotes = successfulQuotes.flatMap(q => {
            if (q.quotes && q.quotes.length > 0) {
              return q.quotes.map(quote => ({
                ...quote,
                courier_type: q.courier_type,
                service_level: q.service_level
              }));
            } else if (q.price !== undefined) {
              // Price is directly on the quote object
              return [{
                price: q.price,
                estimated_transit_time: q.estimated_transit_time,
                courier_type: q.courier_type,
                service_level: q.service_level
              }];
            }
            return [];
          });

          // Get the cheapest quote
          const cheapestQuote = allQuotes.sort((a, b) =>
            parseFloat(a.price) - parseFloat(b.price)
          )[0];

          // Parse transit time from Shippit (e.g., "3 business days" -> 3)
          let transitDays = 5; // Default fallback
          if (cheapestQuote.estimated_transit_time) {
            const match = cheapestQuote.estimated_transit_time.match(/(\d+)/);
            if (match) {
              transitDays = parseInt(match[1], 10);
            }
          }

          const rate = {
            price: cheapestQuote.price,
            name: cheapestQuote.courier_type || cheapestQuote.service_level || 'Standard Shipping',
            transitDays: transitDays
          };

          // console.log('[ShippingEstimator] Found Shippit rate:', JSON.stringify(rate));
          ratesCache.set(cacheKey, rate);
          return rate;
        }

        // console.log('[ShippingEstimator] No successful quotes found');
        return null;
      }

      // console.log('[ShippingEstimator] No Shippit quotes found');
      return null;

    } catch (error) {
      console.error('[ShippingEstimator] Shippit fetch error:', error);
      return null;
    }
  }

  /**
   * Save postcode to localStorage and set body attribute for CSS fallback
   */
  function savePostcode(postcode) {
    try {
      localStorage.setItem(STORAGE_KEY, postcode);
      // Also set body attribute for CSS fallback (persists across morphs)
      document.body.setAttribute('data-has-postcode', postcode);
    } catch (e) {
      console.warn('Could not save postcode to localStorage');
    }
  }

  /**
   * Get saved postcode from localStorage
   */
  function getSavedPostcode() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  /**
   * Get saved suburb from localStorage
   */
  function getSavedSuburb() {
    try {
      return localStorage.getItem('cb_shipping_suburb');
    } catch (e) {
      return null;
    }
  }

  /**
   * Save suburb to localStorage and set body attribute for CSS fallback
   */
  function saveSuburb(suburb) {
    try {
      localStorage.setItem('cb_shipping_suburb', suburb);
      // Also set body attribute for CSS fallback (persists across morphs)
      document.body.setAttribute('data-has-suburb', suburb);
    } catch (e) {
      console.warn('Could not save suburb to localStorage');
    }
  }

  /**
   * Save state to localStorage
   */
  function saveState(state) {
    try {
      localStorage.setItem('cb_shipping_state', state);
    } catch (e) {
      console.warn('Could not save state to localStorage');
    }
  }

  /**
   * Suburb name fixes for postcodes that don't work well with Shippit carriers
   * Some generic suburb names like "Brisbane" cause carrier API issues
   */
  const SUBURB_FIXES = {
    '4000': 'Brisbane City',
    '2000': 'Sydney',
    '3000': 'Melbourne',
    '5000': 'Adelaide',
    '6000': 'Perth'
  };

  /**
   * Lookup suburb from postcode using the Cloudflare Worker
   */
  async function lookupSuburb(postcode) {
    // Check if we have a known fix for this postcode
    if (SUBURB_FIXES[postcode]) {
      // console.log('[ShippingEstimator] Using fixed suburb for postcode', postcode, ':', SUBURB_FIXES[postcode]);
      // Still need to get the state
      const stateMap = {
        '4000': 'QLD',
        '2000': 'NSW',
        '3000': 'VIC',
        '5000': 'SA',
        '6000': 'WA'
      };
      return {
        suburb: SUBURB_FIXES[postcode],
        state: stateMap[postcode]
      };
    }

    try {
      const response = await fetch(SHIPPIT_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'suburb_search',
          query: postcode
        })
      });

      if (!response.ok) return null;

      const data = await response.json();
      if (data.localities && data.localities.locality && data.localities.locality.length > 0) {
        const firstResult = data.localities.locality[0];
        return {
          suburb: firstResult.location,
          state: firstResult.state
        };
      }
      return null;
    } catch (e) {
      // console.log('[ShippingEstimator] Suburb lookup failed:', e);
      return null;
    }
  }

  /**
   * Get zone type for fallback rates (metro, regional, remote)
   */
  function getZoneType(postcode) {
    const zone = getZoneForPostcode(postcode);
    if (zone) return 'metro';

    const state = getStateFromPostcode(postcode);
    // Remote states
    if (['WA', 'SA', 'TAS', 'NT'].includes(state)) return 'remote';
    // Regional (NSW, VIC, QLD outside metro)
    return 'regional';
  }

  /**
   * Major Australian capital-city metros (used for Euro Alfresco flat freight).
   * Covers the SHIPPING_ZONES metros (Sydney, Melbourne, Brisbane, Newcastle,
   * Wollongong, Canberra, Perth) plus the capitals not defined as zones.
   */
  function isMajorMetro(postcode) {
    if (getZoneForPostcode(postcode)) return true;
    const pc = parseInt(postcode, 10);
    if (isNaN(pc)) return false;
    const ranges = [
      [5000, 5199], // Adelaide metro (SA)
      [4210, 4287], // Gold Coast (QLD)
      [7000, 7099], // Hobart metro (TAS)
      [800, 850],   // Darwin / Palmerston (NT)
    ];
    return ranges.some(function (r) { return pc >= r[0] && pc <= r[1]; });
  }

  /**
   * Initialize a shipping estimator component
   * @param {HTMLElement} container - The shipping estimator container
   * @param {boolean} forceReinit - Force re-initialization even if already in WeakSet
   */
  function initEstimator(container, forceReinit = false) {
    // Skip if already initialized using WeakSet (survives DOM morph unlike data attributes)
    // Unless forceReinit is true (used for combined listings product changes)
    if (initializedContainers.has(container) && !forceReinit) {
      // console.log('[ShippingEstimator] Already initialized (WeakSet), skipping duplicate init');
      return;
    }
    initializedContainers.add(container);
    container.dataset.initialized = 'true'; // Also set for CSS/debugging
    // console.log('[ShippingEstimator] Initializing estimator, forceReinit:', forceReinit);

    const input = container.querySelector('.shipping-estimator__postcode-input');
    const checkBtn = container.querySelector('.shipping-estimator__postcode-btn');
    const resultSection = container.querySelector('.shipping-estimator__result');
    const inStockCard = container.querySelector('.shipping-estimator__card--in-stock');
    const supplierCard = container.querySelector('.shipping-estimator__card--supplier');
    const customMadeCard = container.querySelector('.shipping-estimator__card--custom-made');
    const unsupportedCard = container.querySelector('.shipping-estimator__card--unsupported');
    const pickupOnlyCard = container.querySelector('.shipping-estimator__card--pickup-only');
    const sydneyMetroOnlyCard = container.querySelector('.shipping-estimator__card--sydney-metro-only');
    const sydneyMetroUnavailableCard = container.querySelector('.shipping-estimator__card--sydney-metro-unavailable');
    const freeShippingCard = container.querySelector('.shipping-estimator__card--free-shipping');
    const zipShippingFreeCard = container.querySelector('.shipping-estimator__card--zip-shipping-free');
    const alliedFreeCard = container.querySelector('.shipping-estimator__card--allied-free');
    const backorderCard = container.querySelector('.shipping-estimator__card--backorder');
    const sherpaCard = container.querySelector('.shipping-estimator__card--sherpa');
    const euroAlfrescoCard = container.querySelector('.shipping-estimator__card--euro-alfresco');
    const changeButtons = container.querySelectorAll('.shipping-estimator__change-postcode');
    const loadingEl = container.querySelector('.shipping-estimator__loading');

    // Helper function to get current settings from data attributes (re-read on each call)
    function getSettings() {
      return {
        isInStock: container.dataset.inStock === 'true',
        isCustomMade: container.dataset.isCustomMade === 'true',
        isBulky: container.dataset.isBulky === 'true',
        isAlliedExpress: container.dataset.isAlliedExpress === 'true',
        isAustraliaPost: container.dataset.isAustraliaPost === 'true',
        isSydneyMetroOnly: container.dataset.isSydneyMetroOnly === 'true',
        isPickupOnly: container.dataset.isPickupOnly === 'true',
        isCo2Shipping: container.dataset.isCo2Shipping === 'true',
        isZipShipping: container.dataset.isZipShipping === 'true',
        isEuroAlfresco: container.dataset.isEuroAlfresco === 'true',
        euroAlfrescoFreight: parseFloat(container.dataset.euroAlfrescoFreight) || 0,
        qualifiesFreeShipping: container.dataset.freeShipping === 'true',
        bulkySurcharge: parseFloat(container.dataset.bulkySurcharge) || 50,
        bulkyQuoteOnly: container.dataset.bulkyQuoteOnly === 'true',
        vendor: container.dataset.vendor || '',
        sku: container.dataset.sku || '',
        supplierStockUrl: container.dataset.supplierStockUrl || '',
        stockOverride: container.dataset.stockOverride || '',
        fallbackRates: {
          metro: parseFloat(container.dataset.fallbackMetro) || 15,
          regional: parseFloat(container.dataset.fallbackRegional) || 25,
          remote: parseFloat(container.dataset.fallbackRemote) || 45
        }
      };
    }

    // Initial settings (will be re-read dynamically when needed)
    let settings = getSettings();

    // Cache product-level attributes on init (for restoration after morph empties them)
    var productId = container.getAttribute('data-product-id');
    if (productId && settings.vendor) {
      productAttributeCache[productId] = {
        vendor: settings.vendor,
        weight: container.getAttribute('data-weight') || '',
        isBulky: container.getAttribute('data-is-bulky') || '',
        isAlliedExpress: container.getAttribute('data-is-allied-express') || '',
        isAustraliaPost: container.getAttribute('data-is-australia-post') || '',
        isSydneyMetroOnly: container.getAttribute('data-is-sydney-metro-only') || '',
        isPickupOnly: container.getAttribute('data-is-pickup-only') || '',
        isCo2Shipping: container.getAttribute('data-is-co2-shipping') || '',
        isZipShipping: container.getAttribute('data-is-zip-shipping') || ''
      };
    }

    /**
     * Show loading state
     */
    function showLoading() {
      // Hide preview card when loading starts
      const preview = container.querySelector('.shipping-estimator__preview');
      if (preview) preview.style.display = 'none';

      checkBtn.disabled = true;
      checkBtn.textContent = 'Loading...';
      if (loadingEl) loadingEl.style.display = 'block';
    }

    /**
     * Hide loading state
     */
    function hideLoading() {
      checkBtn.disabled = false;
      checkBtn.textContent = 'Check';
      if (loadingEl) loadingEl.style.display = 'none';
    }

    /**
     * Hide all shipping cards
     */
    function hideAllCards() {
      const allCards = [
        inStockCard, supplierCard, customMadeCard, unsupportedCard,
        pickupOnlyCard, sydneyMetroOnlyCard, sydneyMetroUnavailableCard,
        freeShippingCard, zipShippingFreeCard, alliedFreeCard, backorderCard,
        sherpaCard, euroAlfrescoCard
      ];
      allCards.forEach(card => {
        if (card) card.style.display = 'none';
      });
    }

    /**
     * Check if postcode is in Sydney Metro area
     */
    function isSydneyMetroPostcode(postcode) {
      const pc = parseInt(postcode, 10);
      // Sydney Metro postcodes: 2000-2234, 2555-2574, 2745-2770
      return (pc >= 2000 && pc <= 2234) ||
             (pc >= 2555 && pc <= 2574) ||
             (pc >= 2745 && pc <= 2770);
    }

    /**
     * Check if postcode is in a Metro area (Sydney, Melbourne, Brisbane)
     */
    function isMetroPostcode(postcode) {
      const pc = parseInt(postcode, 10);
      // Sydney Metro
      if ((pc >= 2000 && pc <= 2234) || (pc >= 2555 && pc <= 2574) || (pc >= 2745 && pc <= 2770)) return true;
      // Melbourne Metro
      if ((pc >= 3000 && pc <= 3211) || (pc >= 3335 && pc <= 3341) || (pc >= 3427 && pc <= 3442) || (pc >= 3750 && pc <= 3810)) return true;
      // Brisbane Metro
      if ((pc >= 4000 && pc <= 4209) || (pc >= 4300 && pc <= 4347) || (pc >= 4500 && pc <= 4575)) return true;
      return false;
    }

    /**
     * Update the display based on postcode
     */
    async function updateDisplay(postcode) {
      // Re-read settings from data attributes (they may have changed via DOM morph)
      settings = getSettings();
      const { isInStock, isCustomMade, isBulky, isAlliedExpress, isAustraliaPost,
              isSydneyMetroOnly, isPickupOnly, isCo2Shipping, isZipShipping,
              qualifiesFreeShipping, bulkySurcharge, bulkyQuoteOnly, vendor, sku } = settings;

      // Get vendor-specific lead time for supplier/made-to-order items
      let vendorLeadTime = getVendorLeadTime(vendor);

      // Kaldewei non-made-to-order items (e.g. legs) use default lead time
      if (vendor && vendor.toLowerCase().trim() === 'kaldewei' && !isCustomMade) {
        vendorLeadTime = DEFAULT_VENDOR_LEAD_TIME;
      }

      // ============================================
      // STOCK OVERRIDE TAG CHECK (e.g. "stock: 4-6 weeks")
      // Must run BEFORE shipping method branches so all paths respect it
      // ============================================
      let stockOverrideBackorder = false;
      let stockOverrideMinDays = 0;
      let stockOverrideMaxDays = 0;

      if (settings.stockOverride) {
        const parsed = parseStockOverride(settings.stockOverride);
        if (parsed) {
          if (isCustomMade) {
            // Made to Order: always use stock override for delivery dates, never trigger backorder card
            vendorLeadTime = { min: parsed.minDays, max: parsed.maxDays, label: parsed.minDays + '-' + parsed.maxDays + ' business days' };
            // console.log('[ShippingEstimator] Stock override (Made to Order): ' + settings.stockOverride + ' — ' + parsed.minDays + '-' + parsed.maxDays + ' business days');
          } else if (parsed.isBackorder && !isInStock) {
            stockOverrideBackorder = true;
            stockOverrideMinDays = parsed.minDays;
            stockOverrideMaxDays = parsed.maxDays;
            // console.log('[ShippingEstimator] Stock override: backorder (' + settings.stockOverride + ') — ' + parsed.minDays + '-' + parsed.maxDays + ' business days');
          } else if (parsed.isBackorder && isInStock) {
            // Product has stock override tag but is actually in stock — ignore backorder, show normal In Stock
            // console.log('[ShippingEstimator] Stock override: backorder tag ignored (product is in stock)');
          } else {
            vendorLeadTime = { min: parsed.minDays, max: parsed.maxDays, label: parsed.minDays + '-' + parsed.maxDays + ' business days' };
            // console.log('[ShippingEstimator] Stock override: available to order (' + settings.stockOverride + ') — ' + parsed.minDays + '-' + parsed.maxDays + ' business days');
          }
        } else {
          // console.log('[ShippingEstimator] Stock override tag found but could not parse:', settings.stockOverride);
        }
      }

      const zone = getZoneForPostcode(postcode);
      const state = zone ? zone.state : getStateFromPostcode(postcode);

      // Hide all cards first
      hideAllCards();

      // ============================================
      // SHERPA SAME-DAY / NEXT-BUSINESS-DAY QUOTE
      // TEMPORARILY DISABLED — Sherpa integration still in progress.
      // Uncomment to re-enable once ready.
      // ============================================
      // const inventoryQty = parseInt(container.dataset.inventoryQty) || 0;
      // const sherpaSuburb = getSavedSuburb() || '';
      // const sherpaState = state || getStateFromPostcode(postcode) || '';
      // if (isInStock && inventoryQty >= 3 && sherpaCard && sherpaSuburb) {
      //   fetchSherpaQuote(sherpaSuburb, sherpaState, postcode).then(sherpaData => {
      //     if (sherpaData && sherpaData.available) {
      //       const serviceEl = sherpaCard.querySelector('.shipping-estimator__sherpa-service');
      //       const priceEl = sherpaCard.querySelector('.shipping-estimator__sherpa-price');
      //       const descEl = sherpaCard.querySelector('.shipping-estimator__sherpa-description');
      //
      //       if (serviceEl) serviceEl.textContent = sherpaData.serviceName;
      //       if (priceEl) priceEl.textContent = '$' + parseFloat(sherpaData.price).toFixed(2);
      //       if (descEl) {
      //         descEl.textContent = sherpaData.isSameDay
      //           ? 'Order before 1pm for same day delivery via Sherpa'
      //           : 'Delivered next business day via Sherpa';
      //       }
      //
      //       sherpaCard.style.display = 'block';
      //       console.log('[ShippingEstimator] Sherpa quote:', sherpaData.serviceName, '$' + sherpaData.price);
      //     } else {
      //       console.log('[ShippingEstimator] Sherpa not available:', sherpaData ? sherpaData.error : 'no data');
      //     }
      //   });
      // }
      if (sherpaCard) sherpaCard.style.display = 'none';

      // ============================================
      // SPECIAL SHIPPING RULES (check first)
      // ============================================

      // 0. EURO ALFRESCO (Outdoor Kitchen Packages)
      // Per-model flat freight to major metros; "Contact us" elsewhere.
      // Freight amount comes from the product "freight:" tag. (Assembly/install
      // is shown separately near the price, not in the estimator.)
      if (settings.isEuroAlfresco && euroAlfrescoCard) {
        const msgEl = euroAlfrescoCard.querySelector('.shipping-estimator__ea-message');
        const freightLabelEl = euroAlfrescoCard.querySelector('.shipping-estimator__ea-freight-label');
        const freightEl = euroAlfrescoCard.querySelector('.shipping-estimator__ea-freight');
        const pcDisplay = euroAlfrescoCard.querySelector('.shipping-estimator__postcode-display');
        if (pcDisplay) pcDisplay.textContent = postcode;

        if (isMajorMetro(postcode)) {
          if (freightLabelEl) freightLabelEl.textContent = 'Metro Delivery';
          if (freightEl) {
            freightEl.textContent = settings.euroAlfrescoFreight > 0
              ? formatPrice(settings.euroAlfrescoFreight * 100)
              : 'Contact us';
          }
          if (msgEl) msgEl.textContent = 'Flat-rate delivery to your metro area. Made to order — final delivery confirmed after your check measure.';
        } else {
          if (freightLabelEl) freightLabelEl.textContent = 'Delivery';
          if (freightEl) freightEl.textContent = 'Contact us';
          if (msgEl) msgEl.textContent = 'Outside our major metro delivery areas — please contact us for a delivery quote to your location.';
        }

        euroAlfrescoCard.style.display = 'block';
        resultSection.style.display = 'block';
        container.classList.add('has-postcode');
        savePostcode(postcode);
        hideLoading();
        return;
      }

      // 1. PICKUP ONLY - No delivery available
      if (isPickupOnly && pickupOnlyCard) {
        pickupOnlyCard.style.display = 'block';
        // Set stock status (updates dot, text, and card border)
        const statusDot = pickupOnlyCard.querySelector('.shipping-estimator__status-dot');
        const statusText = pickupOnlyCard.querySelector('.shipping-estimator__status-text');
        // Remove any existing status classes from card
        pickupOnlyCard.classList.remove('status-green', 'status-orange', 'status-blue');
        if (isCustomMade) {
          if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--blue';
          if (statusText) statusText.textContent = 'Made to Order';
          pickupOnlyCard.classList.add('status-blue');
        } else if (isInStock) {
          if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--green';
          if (statusText) statusText.textContent = 'In Stock';
          // No class needed - green is default
        } else {
          if (statusDot) statusDot.style.display = 'none';
          if (statusText) statusText.textContent = '';
          pickupOnlyCard.classList.add('status-green');
        }
        resultSection.style.display = 'block';
        container.classList.add('has-postcode');
        savePostcode(postcode);
        hideLoading();
        return;
      }

      // 2. SYDNEY METRO ONLY - Check if postcode is valid
      if (isSydneyMetroOnly && !stockOverrideBackorder) {
        // Helper to set stock status on card (updates dot, text, and card border)
        function setStockStatus(card) {
          const statusDot = card.querySelector('.shipping-estimator__status-dot');
          const statusText = card.querySelector('.shipping-estimator__status-text');
          // Remove any existing status classes from card
          card.classList.remove('status-green', 'status-orange', 'status-blue');
          if (isCustomMade) {
            if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--blue';
            if (statusText) statusText.textContent = 'Made to Order';
            card.classList.add('status-blue');
          } else if (isInStock) {
            if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--green';
            if (statusText) statusText.textContent = 'In Stock';
            // No class needed - green is default
          } else {
            if (statusDot) statusDot.style.display = 'none';
            if (statusText) statusText.textContent = '';
            card.classList.add('status-green');
          }
        }

        if (isSydneyMetroPostcode(postcode)) {
          // Valid Sydney Metro postcode - show delivery card
          if (sydneyMetroOnlyCard) {
            sydneyMetroOnlyCard.style.display = 'block';
            setStockStatus(sydneyMetroOnlyCard);
            const dateRangeEl = sydneyMetroOnlyCard.querySelector('.shipping-estimator__date-range');
            if (dateRangeEl) {
              const minDays = isInStock ? 3 : (isCustomMade ? vendorLeadTime.min : 8);
              const maxDays = isInStock ? 5 : (isCustomMade ? vendorLeadTime.max : 12);
              const minDate = addBusinessDays(new Date(), minDays);
              const maxDate = addBusinessDays(new Date(), maxDays);
              dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
            }
            const postcodeDisplay = sydneyMetroOnlyCard.querySelector('.shipping-estimator__postcode-display');
            if (postcodeDisplay) postcodeDisplay.textContent = postcode;
          }
        } else {
          // Not Sydney Metro - show unavailable message
          if (sydneyMetroUnavailableCard) {
            sydneyMetroUnavailableCard.style.display = 'block';
            setStockStatus(sydneyMetroUnavailableCard);
            const postcodeDisplay = sydneyMetroUnavailableCard.querySelector('.shipping-estimator__postcode-display');
            if (postcodeDisplay) postcodeDisplay.textContent = postcode;
          }
        }
        resultSection.style.display = 'block';
        container.classList.add('has-postcode');
        savePostcode(postcode);
        hideLoading();
        return;
      }

      // 3. CO2 SHIPPING - Free Australia Wide
      if (isCo2Shipping && freeShippingCard && !stockOverrideBackorder) {
        freeShippingCard.style.display = 'block';
        // Set stock status (In Stock / Made to Order / Stocked by Vendor)
        const statusText = freeShippingCard.querySelector('.shipping-estimator__free-status-text');
        const statusDot = freeShippingCard.querySelector('.shipping-estimator__status-dot');
        freeShippingCard.classList.remove('status-green', 'status-orange', 'status-blue');
        if (isInStock) {
          if (statusText) statusText.textContent = 'In Stock';
          if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--green';
        } else if (isCustomMade) {
          if (statusText) statusText.textContent = 'Made to Order';
          if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--blue';
          freeShippingCard.classList.add('status-blue');
        } else {
          if (statusText) statusText.textContent = '';
          if (statusDot) statusDot.style.display = 'none';
          freeShippingCard.classList.add('status-green');
        }
        const dateRangeEl = freeShippingCard.querySelector('.shipping-estimator__date-range');
        if (dateRangeEl) {
          const minDays = isInStock ? 3 : vendorLeadTime.min;
          const maxDays = isInStock ? 6 : vendorLeadTime.max;
          const minDate = addBusinessDays(new Date(), minDays);
          const maxDate = addBusinessDays(new Date(), maxDays);
          dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
        }
        const postcodeDisplay = freeShippingCard.querySelector('.shipping-estimator__postcode-display');
        if (postcodeDisplay) postcodeDisplay.textContent = postcode;
        resultSection.style.display = 'block';
        container.classList.add('has-postcode');
        savePostcode(postcode);
        hideLoading();
        return;
      }

      // 4. ZIP SHIPPING - Free to Sydney, Brisbane, Melbourne Metro
      if (isZipShipping && !stockOverrideBackorder) {
        if (isMetroPostcode(postcode) && zipShippingFreeCard) {
          zipShippingFreeCard.style.display = 'block';
          const statusText = zipShippingFreeCard.querySelector('.shipping-estimator__free-status-text');
          const statusDot = zipShippingFreeCard.querySelector('.shipping-estimator__status-dot');
          zipShippingFreeCard.classList.remove('status-green', 'status-orange', 'status-blue');
          if (isInStock) {
            if (statusText) statusText.textContent = 'In Stock';
            if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--green';
          } else if (isCustomMade) {
            if (statusText) statusText.textContent = 'Made to Order';
            if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--blue';
            zipShippingFreeCard.classList.add('status-blue');
          } else {
            if (statusText) statusText.textContent = '';
            if (statusDot) statusDot.style.display = 'none';
            zipShippingFreeCard.classList.add('status-green');
          }
          const dateRangeEl = zipShippingFreeCard.querySelector('.shipping-estimator__date-range');
          if (dateRangeEl) {
            const minDays = isInStock ? 3 : vendorLeadTime.min;
            const maxDays = isInStock ? 5 : vendorLeadTime.max;
            const minDate = addBusinessDays(new Date(), minDays);
            const maxDate = addBusinessDays(new Date(), maxDays);
            dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
          }
          const postcodeDisplay = zipShippingFreeCard.querySelector('.shipping-estimator__postcode-display');
          if (postcodeDisplay) postcodeDisplay.textContent = postcode;
          resultSection.style.display = 'block';
          container.classList.add('has-postcode');
          savePostcode(postcode);
          hideLoading();
          return;
        }
        // Non-metro - fall through to Shippit calculation
      }

      // 5. ALLIED EXPRESS - Free Sydney Metro, Shippit for others
      // Skip free shipping shortcut when stock override says backorder
      if (isAlliedExpress && isAlliedExpressFreePostcode(postcode) && alliedFreeCard && !stockOverrideBackorder) {
        alliedFreeCard.style.display = 'block';
        // Set stock status (In Stock / Made to Order / Stocked by Vendor)
        const statusText = alliedFreeCard.querySelector('.shipping-estimator__free-status-text');
        const statusDot = alliedFreeCard.querySelector('.shipping-estimator__status-dot');
        alliedFreeCard.classList.remove('status-green', 'status-orange', 'status-blue');
        if (isInStock) {
          if (statusText) statusText.textContent = 'In Stock';
          if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--green';
        } else if (isCustomMade) {
          if (statusText) statusText.textContent = 'Made to Order';
          if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--blue';
          alliedFreeCard.classList.add('status-blue');
        } else {
          if (statusText) statusText.textContent = '';
          if (statusDot) statusDot.style.display = 'none';
          alliedFreeCard.classList.add('status-green');
        }
        const dateRangeEl = alliedFreeCard.querySelector('.shipping-estimator__date-range');
        if (dateRangeEl) {
          const minDays = isInStock ? 3 : vendorLeadTime.min;
          const maxDays = isInStock ? 5 : vendorLeadTime.max;
          const minDate = addBusinessDays(new Date(), minDays);
          const maxDate = addBusinessDays(new Date(), maxDays);
          dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
        }
        const postcodeDisplay = alliedFreeCard.querySelector('.shipping-estimator__postcode-display');
        if (postcodeDisplay) postcodeDisplay.textContent = postcode;
        resultSection.style.display = 'block';
        container.classList.add('has-postcode');
        savePostcode(postcode);
        hideLoading();
        return;
      }

      // 6. AUSTRALIA POST - Free over $800
      if (qualifiesFreeShipping && isAustraliaPost && freeShippingCard && !stockOverrideBackorder) {
        freeShippingCard.style.display = 'block';
        // Set stock status (In Stock / Made to Order / Stocked by Vendor)
        const statusText = freeShippingCard.querySelector('.shipping-estimator__free-status-text');
        const statusDot = freeShippingCard.querySelector('.shipping-estimator__status-dot');
        freeShippingCard.classList.remove('status-green', 'status-orange', 'status-blue');
        if (isInStock) {
          if (statusText) statusText.textContent = 'In Stock';
          if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--green';
        } else if (isCustomMade) {
          if (statusText) statusText.textContent = 'Made to Order';
          if (statusDot) statusDot.className = 'shipping-estimator__status-dot shipping-estimator__status-dot--blue';
          freeShippingCard.classList.add('status-blue');
        } else {
          if (statusText) statusText.textContent = '';
          if (statusDot) statusDot.style.display = 'none';
          freeShippingCard.classList.add('status-green');
        }
        const dateRangeEl = freeShippingCard.querySelector('.shipping-estimator__date-range');
        if (dateRangeEl) {
          const minDays = isInStock ? 3 : vendorLeadTime.min;
          const maxDays = isInStock ? 6 : vendorLeadTime.max;
          const minDate = addBusinessDays(new Date(), minDays);
          const maxDate = addBusinessDays(new Date(), maxDays);
          dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
        }
        const postcodeDisplay = freeShippingCard.querySelector('.shipping-estimator__postcode-display');
        if (postcodeDisplay) postcodeDisplay.textContent = postcode;
        resultSection.style.display = 'block';
        container.classList.add('has-postcode');
        savePostcode(postcode);
        hideLoading();
        return;
      }

      // ============================================
      // GWA STOCK CHECK (for Caroma products)
      // Only check GWA when Cass Brothers is OUT of stock.
      // If Cass Brothers has stock (isInStock=true), skip GWA entirely.
      // ============================================
      let gwaBackorder = false;
      let gwaEstimatedShipDate = null;

      if (isGwaVendor(vendor) && sku && !isInStock) {
        // console.log('[ShippingEstimator] GWA vendor detected (Cass out of stock):', vendor, 'SKU:', sku);
        const gwaStock = await fetchGwaStock(sku);

        if (gwaStock) {
          if (gwaStock.quantity > 0) {
            // GWA Sydney has stock - keep normal "Stocked by Vendor" flow
            // console.log('[ShippingEstimator] GWA Sydney stock available:', gwaStock.quantity, '- using supplier flow');
          } else {
            // GWA Sydney also out of stock - mark as backorder and fetch delivery estimate
            // console.log('[ShippingEstimator] GWA Sydney out of stock, fetching delivery estimate...');
            gwaBackorder = true;

            const deliveryEstimate = await fetchGwaDeliveryEstimate(sku, 1);
            if (deliveryEstimate && deliveryEstimate.estimatedShipDate) {
              gwaEstimatedShipDate = deliveryEstimate.estimatedShipDate;
              // console.log('[ShippingEstimator] GWA estimated ship date:', gwaEstimatedShipDate);
            }
          }
        } else {
          // GWA API failed - fall back to existing behaviour (no change)
          // console.log('[ShippingEstimator] GWA API unavailable, using fallback behaviour');
        }
      } else if (isGwaVendor(vendor) && isInStock) {
        // console.log('[ShippingEstimator] GWA vendor but Cass has stock - showing In Stock');
      }

      // ============================================
      // PHOENIX SUPPLIER STOCK CHECK
      // Only check when Phoenix vendor product is OUT of stock at Cass.
      // If Cass has stock (isInStock=true), skip supplier check entirely.
      // ============================================
      let phoenixBackorder = false;
      let phoenixBackorderMinDays = null;
      let phoenixBackorderMaxDays = null;

      if (isPhoenixVendor(vendor) && sku && !isInStock) {
        // console.log('[ShippingEstimator] Phoenix vendor detected (Cass out of stock):', vendor, 'SKU:', sku);

        const stockData = await fetchSupplierStock(settings.supplierStockUrl);
        if (stockData) {
          const stockStatus = getSupplierStockStatus(stockData, sku);
          // console.log('[ShippingEstimator] Phoenix stock status for', sku, ':', stockStatus || 'not found');

          if (stockStatus === 'In Stock') {
            // Phoenix has stock - use normal supplier flow ("Available to Order")
            // console.log('[ShippingEstimator] Phoenix has stock - using supplier flow');
          } else {
            // Phoenix does NOT have stock (2-4 Weeks, Enquiry, or not found) - backorder
            phoenixBackorder = true;

            if (stockStatus === '2-4 Weeks') {
              // Known restock timeline
              phoenixBackorderMinDays = 10;
              phoenixBackorderMaxDays = 20;
              // console.log('[ShippingEstimator] Phoenix 2-4 Weeks - backorder with 10-20 business days');
            } else {
              // Enquiry, not found, or unknown - use vendor lead times
              phoenixBackorderMinDays = vendorLeadTime.min;
              phoenixBackorderMaxDays = vendorLeadTime.max;
              // console.log('[ShippingEstimator] Phoenix', stockStatus || 'not found', '- backorder with vendor lead time:', vendorLeadTime.label);
            }
          }
        } else {
          // Supplier stock JSON unavailable - fall back to normal supplier flow
          // console.log('[ShippingEstimator] Supplier stock JSON unavailable, using fallback supplier flow');
        }
      } else if (isPhoenixVendor(vendor) && isInStock) {
        // console.log('[ShippingEstimator] Phoenix vendor but Cass has stock - showing In Stock');
      }

      // ============================================
      // STANDARD SHIPPING (Shippit calculation)
      // ============================================

      // Show appropriate card based on stock status
      let activeCard;
      if ((gwaBackorder || phoenixBackorder || stockOverrideBackorder) && backorderCard) {
        // Supplier says out of stock - show backorder card
        activeCard = backorderCard;

        // Set estimated delivery date
        if (gwaBackorder && gwaEstimatedShipDate) {
          // GWA provides a specific ship date from their delivery estimator API
          const shipDate = new Date(gwaEstimatedShipDate);
          const dateRangeEl = backorderCard.querySelector('.shipping-estimator__date-range');
          if (dateRangeEl && !isNaN(shipDate.getTime())) {
            // Add transit time (3-5 business days from ship date to delivery)
            const deliveryMinDate = addBusinessDays(shipDate, 3);
            const deliveryMaxDate = addBusinessDays(shipDate, 5);
            dateRangeEl.textContent = `${formatDate(deliveryMinDate)} - ${formatDate(deliveryMaxDate)}`;
          } else if (dateRangeEl) {
            // Ship date couldn't be parsed - use vendor lead time
            const minDate = addBusinessDays(new Date(), vendorLeadTime.min);
            const maxDate = addBusinessDays(new Date(), vendorLeadTime.max);
            dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
          }
        } else if (phoenixBackorder) {
          // Phoenix uses calculated business days
          const dateRangeEl = backorderCard.querySelector('.shipping-estimator__date-range');
          if (dateRangeEl) {
            const minDate = addBusinessDays(new Date(), phoenixBackorderMinDays);
            const maxDate = addBusinessDays(new Date(), phoenixBackorderMaxDays);
            dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
          }
        } else if (stockOverrideBackorder) {
          // Stock override tag (e.g. "stock: 4-6 weeks") — use parsed business days
          const dateRangeEl = backorderCard.querySelector('.shipping-estimator__date-range');
          if (dateRangeEl) {
            const minDate = addBusinessDays(new Date(), stockOverrideMinDays);
            const maxDate = addBusinessDays(new Date(), stockOverrideMaxDays);
            dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
          }
        }

        // Update postcode and shipping cost on backorder card
        const postcodeEl = backorderCard.querySelector('.shipping-estimator__postcode-display');
        if (postcodeEl) postcodeEl.textContent = postcode;
        const costEl = backorderCard.querySelector('.shipping-estimator__cost-value');
        if (costEl) {
          costEl.textContent = 'Calculated at checkout';
          costEl.classList.remove('loading');
        }

        backorderCard.style.display = 'block';
        resultSection.style.display = 'block';
        container.classList.add('has-postcode');
        savePostcode(postcode);
        hideLoading();
        return;
      } else if (isInStock) {
        activeCard = inStockCard;
      } else if (isCustomMade && customMadeCard) {
        activeCard = customMadeCard;
      } else {
        activeCard = supplierCard;
      }
      activeCard.style.display = 'block';

      // Get date range element (will be updated after Shippit response)
      const dateRangeEl = activeCard.querySelector('.shipping-estimator__date-range');
      if (dateRangeEl) {
        dateRangeEl.textContent = 'Calculating...';
      }

      // Update postcode display
      const postcodeDisplay = activeCard.querySelector('.shipping-estimator__postcode-display');
      if (postcodeDisplay) {
        postcodeDisplay.textContent = postcode;
      }

      // Show loading state for shipping cost
      const costValueEl = activeCard.querySelector('.shipping-estimator__cost-value');
      if (costValueEl) {
        costValueEl.textContent = 'Calculating...';
        costValueEl.classList.add('loading');
        costValueEl.classList.remove('free');
      }

      resultSection.style.display = 'block';
      container.classList.add('has-postcode');

      // Save postcode
      savePostcode(postcode);

      // Note: We no longer dispatch postcodeUpdated from updateDisplay to prevent recursion.
      // The event should be dispatched by the original source of the postcode change
      // (e.g., the header location modal, or the check button click handler).

      // Handle bulky items - quote only mode
      if (isBulky && bulkyQuoteOnly) {
        if (costValueEl) {
          costValueEl.textContent = 'Quote on request';
          costValueEl.classList.remove('loading');
          costValueEl.classList.add('quote-required');
        }
        // Show estimated delivery date range for bulky items
        // Use vendor-specific lead time for non-stock items
        if (dateRangeEl) {
          const minDays = isInStock ? 9 : vendorLeadTime.min;
          const maxDays = isInStock ? 12 : vendorLeadTime.max;
          const minDate = addBusinessDays(new Date(), minDays);
          const maxDate = addBusinessDays(new Date(), maxDays);
          dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
        }
        hideLoading();
        return;
      }

      // Handle free shipping (Australia Post threshold or Allied Express Sydney Metro)
      const alliedExpressFree = isAlliedExpress && isAlliedExpressFreePostcode(postcode);
      if (qualifiesFreeShipping || alliedExpressFree) {
        if (costValueEl) {
          costValueEl.textContent = 'FREE';
          costValueEl.classList.remove('loading');
          costValueEl.classList.add('free');
        }
        // Show estimated delivery date range for free shipping
        // Use vendor-specific lead time for non-stock items
        if (dateRangeEl) {
          const minDays = isInStock ? 3 : vendorLeadTime.min;
          const maxDays = isInStock ? 5 : vendorLeadTime.max;
          const minDate = addBusinessDays(new Date(), minDays);
          const maxDate = addBusinessDays(new Date(), maxDays);
          dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
        }
        hideLoading();
        return;
      }

      // Fetch shipping rates from Shippit
      try {
        // Get product weight from data attribute (default to 1kg)
        const weight = parseFloat(container.dataset.weight) || 1;
        // For Australia Post tagged products, prefer Eparcel carrier
        const preferredCarrier = isAustraliaPost ? 'eparcel' : null;

        // Always lookup suburb for the current postcode to ensure it's correct
        // (the postcode may have changed since last lookup)
        // console.log('[ShippingEstimator] Looking up suburb for postcode:', postcode);
        let suburb = '';
        const lookupResult = await lookupSuburb(postcode);
        if (lookupResult) {
          suburb = lookupResult.suburb;
          // Save the new suburb and state
          saveSuburb(suburb);
          saveState(lookupResult.state);
          // console.log('[ShippingEstimator] Found suburb:', suburb, 'state:', lookupResult.state);
        } else {
          // Fallback to saved suburb if lookup fails
          suburb = getSavedSuburb() || '';
          // console.log('[ShippingEstimator] Lookup failed, using saved suburb:', suburb);
        }

        const rate = await fetchShippingRates(postcode, state, suburb, weight, preferredCarrier);

        if (rate && costValueEl) {
          let priceDollars = parseFloat(rate.price);

          // Add bulky surcharge if applicable
          if (isBulky) {
            priceDollars += bulkySurcharge;
          }

          const priceCents = priceDollars * 100;
          costValueEl.textContent = formatPrice(priceCents);
          costValueEl.classList.remove('loading');
          if (priceCents === 0) {
            costValueEl.classList.add('free');
          }

          // Update delivery date range using Shippit transit time
          // In-stock: transit only (no picking days)
          // Supplier/Made-to-order: use vendor-specific lead time (already includes picking + shipping)
          if (dateRangeEl) {
            let minDays, maxDays;
            const transitDays = rate.transitDays || 5; // Default 5 days if not provided

            if (isInStock) {
              // In stock: transit time from Shippit only (no picking days)
              const pickingDays = 0;
              minDays = pickingDays + transitDays;
              maxDays = minDays + 2; // Add 2 day buffer for range
            } else {
              // Supplier/Made-to-order: use vendor-specific lead time
              minDays = vendorLeadTime.min;
              maxDays = vendorLeadTime.max;
            }

            const minDate = addBusinessDays(new Date(), minDays);
            const maxDate = addBusinessDays(new Date(), maxDays);
            dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
            // console.log('[ShippingEstimator] Delivery estimate:', isInStock ? 'in-stock' : 'vendor=' + vendor, minDays, '-', maxDays, 'days');
          }
        } else if (costValueEl) {
          // Fallback to "Calculated at checkout" if Shippit fails
          // console.log('[ShippingEstimator] No Shippit rate, using checkout fallback');
          costValueEl.textContent = 'Calculated at checkout';
          costValueEl.classList.remove('loading');

          // Use fallback delivery estimate range
          // Use vendor-specific lead time for non-stock items
          if (dateRangeEl) {
            const minDays = isInStock ? 7 : vendorLeadTime.min;
            const maxDays = isInStock ? 10 : vendorLeadTime.max;
            const minDate = addBusinessDays(new Date(), minDays);
            const maxDate = addBusinessDays(new Date(), maxDays);
            dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
          }
        }
      } catch (error) {
        console.error('[ShippingEstimator] Display error:', error);
        if (costValueEl) {
          // Fallback to "Calculated at checkout" on error
          costValueEl.textContent = 'Calculated at checkout';
          costValueEl.classList.remove('loading');
        }
        // Use fallback delivery estimate range on error
        // Use vendor-specific lead time for non-stock items
        if (dateRangeEl) {
          const minDays = isInStock ? 7 : vendorLeadTime.min;
          const maxDays = isInStock ? 10 : vendorLeadTime.max;
          const minDate = addBusinessDays(new Date(), minDays);
          const maxDate = addBusinessDays(new Date(), maxDays);
          dateRangeEl.textContent = `${formatDate(minDate)} - ${formatDate(maxDate)}`;
        }
      }

      hideLoading();

    }

    /**
     * Reset to postcode input view (show preview card with input)
     */
    function resetToInput() {
      // Hide result section
      resultSection.style.display = 'none';
      container.classList.remove('has-postcode');

      // Show the preview card with postcode input
      const preview = container.querySelector('.shipping-estimator__preview');
      if (preview) {
        preview.style.display = 'block';
        const previewInput = preview.querySelector('.shipping-estimator__postcode-input');
        if (previewInput) {
          previewInput.value = input.value || '';
          previewInput.focus();
          previewInput.select();
        }
      }
    }

    // Event: Check button click
    checkBtn.addEventListener('click', async function() {
      const postcode = input.value.trim();
      if (postcode.length === 4 && /^\d{4}$/.test(postcode)) {
        showLoading();
        globalUpdatingPostcode = true;
        lastUpdateTimestamp = Date.now();
        try {
          await updateDisplay(postcode);
          // Dispatch event for other components (header location, etc.)
          // This is a user-initiated change, so we want to notify others
          // Don't send old suburb - let header modal look up the new suburb for this postcode
          const savedState = localStorage.getItem('cb_shipping_state') || getStateFromPostcode(postcode);
          document.dispatchEvent(new CustomEvent('postcodeUpdated', {
            detail: {
              postcode,
              suburb: null,
              state: savedState
            }
          }));
        } finally {
          globalUpdatingPostcode = false;
        }
      } else {
        input.focus();
        input.classList.add('error');
        setTimeout(() => input.classList.remove('error'), 1000);
      }
    });

    // Event: Enter key in input
    input.addEventListener('keypress', function(e) {
      if (e.key === 'Enter') {
        checkBtn.click();
      }
    });

    // Event: Only allow numbers
    input.addEventListener('input', function() {
      this.value = this.value.replace(/\D/g, '').slice(0, 4);
    });

    // Event: Change postcode button
    changeButtons.forEach(btn => {
      btn.addEventListener('click', resetToInput);
    });

    // Track if we've loaded a postcode to prevent duplicate calls
    let hasLoadedPostcode = false;

    // Listen for postcode updates from header location modal or other sources
    // IMPORTANT: Only register ONE document-level listener to prevent recursion from multiple listeners
    if (!postcodeListenerRegistered) {
      postcodeListenerRegistered = true;
      // console.log('[ShippingEstimator] Registering global postcodeUpdated listener');

      document.addEventListener('postcodeUpdated', function(e) {
        // Skip if this event was dispatched from within updateDisplay (prevents infinite recursion)
        if (globalUpdatingPostcode) {
          // console.log('[ShippingEstimator] Skipping postcodeUpdated - globalUpdatingPostcode is true');
          return;
        }

        // Debounce: skip if we just processed an update within the last 100ms
        const now = Date.now();
        if (now - lastUpdateTimestamp < 100) {
          // console.log('[ShippingEstimator] Skipping postcodeUpdated - debounce (last update was', now - lastUpdateTimestamp, 'ms ago)');
          return;
        }

        const postcode = e.detail && e.detail.postcode;
        // console.log('[ShippingEstimator] Received postcodeUpdated event - postcode:', postcode);
        if (postcode) {
          // Update ALL estimator inputs on the page
          globalUpdatingPostcode = true;
          lastUpdateTimestamp = now;

          const allEstimators = document.querySelectorAll('.shipping-estimator[data-initialized="true"]');
          // console.log('[ShippingEstimator] Updating', allEstimators.length, 'estimators');

          allEstimators.forEach(function(est) {
            const estInput = est.querySelector('.shipping-estimator__postcode-input');
            if (estInput) {
              estInput.value = postcode;
            }
          });

          // Restore display for all estimators, then clear the flag
          checkAndRestoreAll();

          // Clear flag after a short delay to allow any async operations to complete
          setTimeout(function() {
            globalUpdatingPostcode = false;
          }, 100);
        }
      });
    }

    // Function to check and load saved postcode
    function checkAndLoadSavedPostcode() {
      if (hasLoadedPostcode) {
        // console.log('[ShippingEstimator] Already loaded postcode for this instance, skipping');
        return true;
      }

      // Skip if already updating (prevents race conditions)
      if (globalUpdatingPostcode) {
        // console.log('[ShippingEstimator] Skipping checkAndLoadSavedPostcode - globalUpdatingPostcode true');
        return false;
      }

      // Skip if another estimator already started loading the postcode
      // This prevents multiple estimators from all calling updateDisplay simultaneously
      // The periodic checkAndRestoreAll will handle updating this estimator's display
      if (globalPostcodeLoadStarted) {
        // console.log('[ShippingEstimator] Another estimator already loading, will be updated by checkAndRestoreAll');
        const savedPostcode = getSavedPostcode();
        if (savedPostcode && input) {
          input.value = savedPostcode;
          hasLoadedPostcode = true;
        }
        return true; // Return true to prevent retry loops
      }

      const savedPostcode = getSavedPostcode();
      // console.log('[ShippingEstimator] checkAndLoadSavedPostcode - postcode:', savedPostcode);
      if (savedPostcode) {
        input.value = savedPostcode;
        hasLoadedPostcode = true;
        globalPostcodeLoadStarted = true; // Mark that we're loading
        globalUpdatingPostcode = true;
        lastUpdateTimestamp = Date.now();
        updateDisplay(savedPostcode).finally(function() {
          globalUpdatingPostcode = false;
        });
        return true;
      }
      return false;
    }

    // Check for saved postcode immediately
    // console.log('[ShippingEstimator] Initializing estimator, checking for saved postcode...');
    if (!checkAndLoadSavedPostcode()) {
      // If not found, try again after short delays (localStorage may not be ready)
      setTimeout(() => {
        // console.log('[ShippingEstimator] Retry 1 - checking for saved postcode...');
        if (!checkAndLoadSavedPostcode()) {
          // Final attempt after page fully loads
          setTimeout(() => {
            // console.log('[ShippingEstimator] Retry 2 - checking for saved postcode...');
            checkAndLoadSavedPostcode();
          }, 500);
        }
      }, 100);
    }

    // Watch for attribute changes on the container (from variant picker DOM morph)
    // DEBOUNCED: Wait 800ms for morph + attribute correction to settle before refreshing.
    // Without debounce, the morph immediately triggers updateDisplay with stale/wrong attributes
    // AND sets globalUpdatingPostcode=true which blocks the later forceRefreshAll correction.
    var attrDebounceTimer = null;
    const attrObserver = new MutationObserver(function(mutations) {
      const relevantChange = mutations.some(m =>
        m.type === 'attributes' &&
        m.attributeName &&
        m.attributeName.startsWith('data-') &&
        m.attributeName !== 'data-initialized'
      );

      if (relevantChange && hasLoadedPostcode && !globalUpdatingPostcode) {
        if (attrDebounceTimer) clearTimeout(attrDebounceTimer);
        attrDebounceTimer = setTimeout(function() {
          // Correct variant-level attributes before refreshing (morph may have set default variant data)
          correctVariantAttributesFromDom();

          const currentPostcode = getSavedPostcode();
          if (currentPostcode && !globalUpdatingPostcode) {
            // console.log('[ShippingEstimator] Data attributes changed (debounced), refreshing display...');
            globalUpdatingPostcode = true;
            updateDisplay(currentPostcode).finally(function() {
              globalUpdatingPostcode = false;
            });
          }
        }, 800);
      }
    });

    attrObserver.observe(container, { attributes: true });

    // Expose updateDisplay on the container so variant change handlers can trigger it directly
    container._shippingUpdateDisplay = updateDisplay;
  }

  /**
   * Correct variant-level data attributes on shipping estimators after a morph.
   * After updateMain morphs <main> (combined listings), the morphed HTML may contain
   * default/stale variant data. This reads the correct variant from the URL + variant-picker
   * JSON and fixes the attributes. Also restores cached product-level attributes if morph cleared them.
   */
  function correctVariantAttributesFromDom() {
    var variantId = new URLSearchParams(window.location.search).get('variant');
    if (!variantId) return;

    // Find the variant data from variant-picker's JSON (updated by morph to reflect new product)
    var jsonScript = document.querySelector('variant-picker script[type="application/json"]');
    if (!jsonScript) return;

    try {
      var allVariants = JSON.parse(jsonScript.textContent);
      var selectedVariant = null;
      for (var i = 0; i < allVariants.length; i++) {
        if (String(allVariants[i].id) === variantId) {
          selectedVariant = allVariants[i];
          break;
        }
      }
      if (!selectedVariant) return;

      document.querySelectorAll('.shipping-estimator').forEach(function(est) {
        var changed = false;

        // Fix variant-level attributes from the JSON (these are accurate for the selected variant)
        if (selectedVariant.id != null && est.getAttribute('data-variant-id') !== String(selectedVariant.id)) {
          est.setAttribute('data-variant-id', String(selectedVariant.id));
          changed = true;
        }
        if (selectedVariant.sku != null && est.getAttribute('data-sku') !== String(selectedVariant.sku)) {
          est.setAttribute('data-sku', String(selectedVariant.sku));
          changed = true;
        }
        if (selectedVariant.price != null && est.getAttribute('data-price') !== String(selectedVariant.price)) {
          est.setAttribute('data-price', String(selectedVariant.price));
          changed = true;
        }

        // Restore product-level attributes from cache if morph cleared them
        var pid = est.getAttribute('data-product-id');
        if (pid && productAttributeCache[pid]) {
          var cache = productAttributeCache[pid];
          if (!est.getAttribute('data-vendor') && cache.vendor) {
            est.setAttribute('data-vendor', cache.vendor);
            changed = true;
            // console.log('[ShippingEstimator] Restored cached vendor:', cache.vendor);
          }
          // Restore other product-level attributes if cleared
          var productAttrs = ['data-weight', 'data-is-bulky', 'data-is-allied-express',
            'data-is-australia-post', 'data-is-sydney-metro-only', 'data-is-pickup-only',
            'data-is-co2-shipping', 'data-is-zip-shipping'];
          var cacheKeys = ['weight', 'isBulky', 'isAlliedExpress', 'isAustraliaPost',
            'isSydneyMetroOnly', 'isPickupOnly', 'isCo2Shipping', 'isZipShipping'];
          for (var j = 0; j < productAttrs.length; j++) {
            if (!est.getAttribute(productAttrs[j]) && cache[cacheKeys[j]]) {
              est.setAttribute(productAttrs[j], cache[cacheKeys[j]]);
            }
          }
        }

        // Update cache with current product-level attributes (if they're valid)
        if (pid && est.getAttribute('data-vendor')) {
          productAttributeCache[pid] = {
            vendor: est.getAttribute('data-vendor'),
            weight: est.getAttribute('data-weight') || '',
            isBulky: est.getAttribute('data-is-bulky') || '',
            isAlliedExpress: est.getAttribute('data-is-allied-express') || '',
            isAustraliaPost: est.getAttribute('data-is-australia-post') || '',
            isSydneyMetroOnly: est.getAttribute('data-is-sydney-metro-only') || '',
            isPickupOnly: est.getAttribute('data-is-pickup-only') || '',
            isCo2Shipping: est.getAttribute('data-is-co2-shipping') || '',
            isZipShipping: est.getAttribute('data-is-zip-shipping') || ''
          };
        }

        if (changed) {
          // console.log('[ShippingEstimator] Corrected variant attributes: id=' + selectedVariant.id + ' sku=' + selectedVariant.sku);
        }
      });
    } catch (e) {
      console.warn('[ShippingEstimator] Error correcting variant attributes:', e);
    }
  }

  /**
   * Initialize all shipping estimators on the page
   */
  function init() {
    const estimators = document.querySelectorAll('.shipping-estimator');
    estimators.forEach(initEstimator);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on pageshow (handles bfcache when navigating back)
  window.addEventListener('pageshow', function(event) {
    if (event.persisted) {
      // console.log('[ShippingEstimator] Page restored from bfcache, re-initializing...');
      init();
    }
  });

  // Also listen for Shopify section rendering (for AJAX navigation)
  document.addEventListener('shopify:section:load', init);

  // Listen for variant picker updates (custom event dispatched after variant change)
  document.addEventListener('variant:update', function(event) {
    // console.log('[ShippingEstimator] variant:update event received');

    // Reset global flag so new/morphed estimators can call updateDisplay with the new SKU
    globalPostcodeLoadStarted = false;

    // Sync variant-level attributes from event.detail.resource (accurate for selected variant).
    // DO NOT use event.detail.data.html — the HTML may contain stale/default variant data
    // because the page fetch happens before variant selection is applied in the DOM.
    try {
      var resource = event && event.detail && event.detail.resource;
      if (resource) {
        var targetProductId = event && event.detail && event.detail.data && event.detail.data.productId;

        document.querySelectorAll('.shipping-estimator').forEach(function(currentEstimator) {
          if (targetProductId) {
            var currentProductId = currentEstimator.getAttribute('data-product-id');
            if (currentProductId && currentProductId !== String(targetProductId)) return;
          }

          var changed = false;
          if (resource.id != null) {
            var newId = String(resource.id);
            if (currentEstimator.getAttribute('data-variant-id') !== newId) {
              currentEstimator.setAttribute('data-variant-id', newId);
              changed = true;
            }
          }
          if (resource.sku != null) {
            var newSku = String(resource.sku);
            if (currentEstimator.getAttribute('data-sku') !== newSku) {
              currentEstimator.setAttribute('data-sku', newSku);
              changed = true;
            }
          }
          if (resource.price != null) {
            var newPrice = String(resource.price);
            if (currentEstimator.getAttribute('data-price') !== newPrice) {
              currentEstimator.setAttribute('data-price', newPrice);
              changed = true;
            }
          }
          // Note: resource.available != data-in-stock (available=purchasable, in-stock=physical stock)
          // We don't overwrite data-in-stock from resource to avoid incorrect stock status
          if (changed) {
            // console.log('[ShippingEstimator] Synced variant attributes from resource. Variant:', resource.id, 'SKU:', resource.sku);
          }
        });
        lastEventDrivenUpdate = Date.now();
      }
    } catch (syncErr) {
      console.warn('[ShippingEstimator] Error syncing variant attributes:', syncErr);
    }

    // Re-mark containers that are in the WeakSet but lost their data-initialized attribute (morph stripped it)
    document.querySelectorAll('.shipping-estimator:not([data-initialized="true"])').forEach(function(container) {
      if (initializedContainers.has(container)) {
        container.dataset.initialized = 'true';
        // console.log('[ShippingEstimator] Re-marked initialized container after morph');
      } else {
        initEstimator(container);
      }
    });

    // Always restore postcode display after variant update (morph may have reset it)
    checkAndRestoreAll();

    // Correct variant attributes and force refresh after morph settles
    setTimeout(function() {
      correctVariantAttributesFromDom();
      forceRefreshAll();
    }, 200);

    // Re-initialize wishlist buttons after variant morph (TT Wishlist app)
    initWishlistButtons();
    lastEventDrivenUpdate = Date.now();
  });

  // Listen for morph:complete event (dispatched after any DOM morph)
  // Note: currently not dispatched by theme code, but kept for future compatibility
  document.addEventListener('morph:complete', function(e) {
    // console.log('[ShippingEstimator] morph:complete event received');

    globalPostcodeLoadStarted = false;
    checkAndRestoreAll();

    document.querySelectorAll('.shipping-estimator:not([data-initialized="true"])').forEach(function(container) {
      if (initializedContainers.has(container)) {
        container.dataset.initialized = 'true';
      } else {
        initEstimator(container);
      }
    });

    // Correct variant attributes from URL + variant-picker JSON, then refresh
    setTimeout(function() {
      correctVariantAttributesFromDom();
      forceRefreshAll();
    }, 200);

    initWishlistButtons();
    lastEventDrivenUpdate = Date.now();
  });

  // Listen for main:morphed event (dispatched after combined listing variant change morphs main)
  // Note: currently not dispatched by theme code, but kept for future compatibility
  document.addEventListener('main:morphed', function(e) {
    // console.log('[ShippingEstimator] main:morphed event received');

    globalPostcodeLoadStarted = false;
    checkAndRestoreAll();

    requestAnimationFrame(function() {
      document.querySelectorAll('.shipping-estimator:not([data-initialized="true"])').forEach(function(container) {
        if (initializedContainers.has(container)) {
          container.dataset.initialized = 'true';
        } else {
          initEstimator(container);
        }
      });
    });

    // Correct variant attributes from URL + variant-picker JSON, then refresh
    setTimeout(function() {
      correctVariantAttributesFromDom();
      forceRefreshAll();
    }, 300);

    setTimeout(function() {
      initWishlistButtons();
      if (window.jdgm && typeof window.jdgm.loadFromPage === 'function') {
        try { window.jdgm.loadFromPage(); } catch (e) {}
      }
    }, 500);
    lastEventDrivenUpdate = Date.now();
  });

  // Fallback: URL watcher (variant parameter AND pathname for combined listings)
  // If variant:update events don't fire (edge cases), detect URL changes and refresh
  (function() {
    var lastVariantParam = new URLSearchParams(window.location.search).get('variant') || '';
    var lastPathname = window.location.pathname;
    setInterval(function() {
      var currentVariantParam = new URLSearchParams(window.location.search).get('variant') || '';
      var currentPathname = window.location.pathname;

      // Detect variant param change OR pathname change (combined listings change the path)
      var variantChanged = currentVariantParam && currentVariantParam !== lastVariantParam;
      var pathChanged = currentPathname !== lastPathname;

      if (variantChanged || pathChanged) {
        lastVariantParam = currentVariantParam;
        lastPathname = currentPathname;

        // Skip if an event handler (variant:update, morph:complete) already handled this recently
        if (Date.now() - lastEventDrivenUpdate < 3000) {
          // console.log('[ShippingEstimator] URL change detected but event handler already handled it, skipping');
          return;
        }

        // console.log('[ShippingEstimator] URL changed (variant:', currentVariantParam, 'path:', currentPathname, ') - triggering refresh');
        globalPostcodeLoadStarted = false;

        // Re-init any un-initialized estimators
        document.querySelectorAll('.shipping-estimator:not([data-initialized="true"])').forEach(function(container) {
          if (!initializedContainers.has(container)) {
            initEstimator(container);
          }
        });

        checkAndRestoreAll();
        // Correct variant attributes from URL + variant-picker JSON, then refresh
        correctVariantAttributesFromDom();
        setTimeout(forceRefreshAll, 300);
      }
    }, 1000);
  })();

  /**
   * Initialize wishlist buttons - handles TT Wishlist app
   * Simplified version - just calls the app's init function if available
   */
  function initWishlistButtons() {
    // Check if there are any empty wishlist containers
    const allBtns = document.querySelectorAll('.wishlist-pdp-btn-main, .tt-wishlist-plp-btn-main');
    let hasEmpty = false;

    allBtns.forEach(function(btn) {
      if (!btn.querySelector('.wishlist-plp-btn-wrap, .wishlist-pdp-btn-wrap, .wishlist-pdp-btn-main-wrap')) {
        hasEmpty = true;
      }
    });

    if (!hasEmpty) return; // All buttons populated

    // Try the app's init functions
    if (typeof window['wishlistButtonsInit'] === 'function') {
      window['wishlistButtonsInit']();
    } else if (typeof window['wishlistInit'] === 'function') {
      window['wishlistInit']();
    }
  }

  // Fast restore function - directly updates UI without triggering API calls
  function fastRestorePostcodeDisplay(container) {
    const savedPostcode = getSavedPostcode();
    if (!savedPostcode) return false;

    const input = container.querySelector('.shipping-estimator__postcode-input');
    const resultSection = container.querySelector('.shipping-estimator__result');
    const postcodeSection = container.querySelector('.shipping-estimator__postcode');

    if (!input || !resultSection) return false;

    // Add class immediately to prevent flicker
    container.classList.add('has-postcode');

    // CRITICAL: Hide postcode input section and show result section
    // The morph may have reset these inline styles
    if (postcodeSection) {
      postcodeSection.style.display = 'none';
    }
    resultSection.style.display = 'block';

    // Update input value
    input.value = savedPostcode;

    // Update postcode display in all cards
    container.querySelectorAll('.shipping-estimator__postcode-display').forEach(function(el) {
      el.textContent = savedPostcode;
    });

    return true;
  }

  /**
   * Force a full refresh of all initialized shipping estimators
   * Called after variant/swatch changes to re-run GWA checks with updated SKU
   */
  function forceRefreshAll() {
    const savedPostcode = getSavedPostcode();
    if (!savedPostcode || globalUpdatingPostcode) return;

    // Find all estimators - don't require data-initialized since morph may have stripped it
    var estimators = document.querySelectorAll('.shipping-estimator');
    estimators.forEach(function(container) {
      if (container._shippingUpdateDisplay) {
        // console.log('[ShippingEstimator] Force refreshing estimator with postcode:', savedPostcode);
        globalUpdatingPostcode = true;
        container._shippingUpdateDisplay(savedPostcode).finally(function() {
          globalUpdatingPostcode = false;
        });
      }
    });
  }

  // Periodic check to restore postcode display after morphs
  // This catches cases where morph resets the display but the element is the same
  // OPTIMIZED: Avoid expensive getComputedStyle calls - use inline style checks instead
  function checkAndRestoreAll() {
    // First ensure body attributes are set (critical for CSS fallback)
    ensureBodyAttributes();

    const savedPostcode = getSavedPostcode();
    if (!savedPostcode) return;

    document.querySelectorAll('.shipping-estimator').forEach(function(container) {
      // Simple class-based check - avoids expensive getComputedStyle
      // If the class is missing, restore it
      if (!container.classList.contains('has-postcode')) {
        fastRestorePostcodeDisplay(container);
      }
    });
  }

  // REMOVED: Continuous animation frame loop was causing severe performance issues
  // The checkAndRestoreAll function is now called only:
  // 1. On specific events (morph:complete, main:morphed, variant:update, postcodeUpdated)
  // 2. On a periodic safety-net interval (every 2 seconds, not 250ms)

  // Periodic safety-net check - runs much less frequently
  // This catches any edge cases where events weren't fired
  setInterval(checkAndRestoreAll, 2000);

  // Expose for external use
  window.ShippingEstimator = {
    getZoneForPostcode,
    getSavedPostcode,
    savePostcode,
    fetchShippingRates,
    isAlliedExpressFreePostcode,
    fetchGwaStock,
    fetchGwaDeliveryEstimate,
    isGwaVendor,
    isPhoenixVendor,
    fetchSupplierStock,
    getSupplierStockStatus,
    SHIPPING_ZONES,
    ALLIED_EXPRESS_FREE_POSTCODES
  };

})();
